import React from 'react'
import styled, { keyframes, css } from 'styled-components'

import Icon, { NavArrowIcon } from '../../components/icon/Icon'
import { palette, fonts, mediaQueries } from '../../styles/variables'
import alarm from '../../styles/assets/alarm_gray.svg'
import { AlertList } from '../Alerts/styled'
import { SeeMoreSection } from '../Dashboard/styled'
import { translate } from '../../utils/translation'
import { LoadingResponsive } from '../../components/loading/Loading'

export const NavBarWrapper = styled.div({
  position: 'relative',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: palette.white,
  borderBottom: `1px solid ${palette.lightGray70}`,
  minHeight: '60px',
  height: '60px',
  padding: '0 20px',
  zIndex: 120,
  [mediaQueries.mobile]: {
    padding: '0 20px',
  },
})

export const NavLogo = styled.div({
  display: 'flex',
  alignItems: 'center',
})

export const NavLogoImg = styled.img({
  width: '108px',
  boxSizing: 'content-box',
  padding: '0 24px 0 0',
  margin: '0 24px 0 0',
  borderRight: `1px solid ${palette.darkGray30}`,
  cursor: 'pointer',
})

export const NavLogoButton = styled.div(({ cursor = 'pointer' }) => ({
  display: 'flex',
  position: 'relative',
  width: '60px',
  height: '40px',
  alignItems: 'center',
  backgroundColor: 'transparent',
  border: 'none',
  padding: '0',
  fontSize: '15px',
  lineHeight: '15px',
  fontFamily: fonts.primaryBook,
  color: palette.darkGray,
  cursor,
}))

export const PricingButton = styled.div(({ isPointer, isOpen = false }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textTransform: 'uppercase',
  fontFamily: fonts.primaryBook,
  color: isOpen ? palette.orange : palette.darkGray,
  height: 15,
  fontSize: '15px',
  lineHeight: '15px',
  cursor: isPointer ? 'default' : 'pointer',
  transition: 'color .3s ease',
}))

export const NavButtonsWrapper = styled.ul({
  display: 'flex',
  height: '100%',
  alignItems: 'center',
})

export const NavItem = styled.li((props) => {
  if (props.languageItem) {
    return {
      display: 'block',
      whiteSpace: 'nowrap',
      border: 0,
      '& > div': {
        fontSize: '12px',
        border: 0,
        color: props.active ? palette.darkGray50 : palette.darkGray,
      },
      ':hover': {
        '& div': {
          fontSize: '12px',
          color: palette.orange,
        },
      },
    }
  }
  return {
    marginLeft: '25px',
    position: 'relative',
    ':hover': {
      '& > ul': {
        opacity: 1,
        visibility: 'visible',
      },
    },
  }
})

export const RightMenu = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
})

export const SettingsPanel = styled.ul({
  position: 'absolute',
  zIndex: 10,
  top: 'calc(100% + 10px)',
  right: 5,
  backgroundColor: palette.white,
  borderRadius: '2px',
  boxShadow: '0 20px 29px 0 rgba(207,67,20,.5)',
  fontFamily: fonts.primaryBold,
  fontWeight: 500,
  padding: 0,
  margin: 0,
  width: 200,
  minWidth: 'fit-content',
})

export const SettingItem = styled.li({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: 40,
  '& > *': {
    width: '100%',
    height: '100%',
  },
})

export const SettingsButton = styled.div(({ cursor = 'pointer' }) => ({
  width: '100%',
  height: '100%',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  padding: '0 16px 0 0',
  fontFamily: fonts.primaryBold,
  fontSize: '12px',
  lineHeight: '12px',
  cursor,
  ':hover': {
    backgroundColor: palette.lightGray20,
  },
  color: 'black',
}))

const pulseColor = 'white'
const dotPulseBefore = keyframes`
  0% {
    box-shadow: 9984px 0 0 -5px ${pulseColor};
  }
  30% {
    box-shadow: 9984px 0 0 2px ${pulseColor};
  }
  60% {
    box-shadow: 9984px 0 0 -5px ${pulseColor};
  }
  100% {
    box-shadow: 9984px 0 0 -5px ${pulseColor};
  }
`
const dotPulse = keyframes`
  0% {
    box-shadow: 9999px 0 0 -5px ${pulseColor};
  }
  30% {
    box-shadow: 9999px 0 0 2px ${pulseColor};
  }
  60% {
    box-shadow: 9999px 0 0 -5px ${pulseColor};
  }
  100% {
    box-shadow: 9999px 0 0 -5px ${pulseColor};
  }
`

const dotPulseAfter = keyframes`
  0% {
    box-shadow: 10014px 0 0 -5px ${pulseColor};
  }
  30% {
    box-shadow: 10014px 0 0 2px ${pulseColor};
  },
  60% {
    box-shadow: 10014px 0 0 -5px ${pulseColor};
  },
  100% {
    box-shadow: 10014px 0 0 -5px ${pulseColor};
  },
`

const pulseAnimation = css`
  ${dotPulse} 1.5s infinite linear;
`
const pulseAnimationBefore = css`
  ${dotPulseBefore} 1.5s infinite linear;
`
const pulseAnimationAfter = css`
  ${dotPulseAfter} 1.5s infinite linear;
`

export const Pulse = styled.div`
  position: relative;
  left: -9999px;
  width: 10px;
  height: 10px;
  border-radius: 5px;
  background-color: pulseColor;
  color: pulseColor;
  box-shadow: 9999px 0 0 -5px ${pulseColor};
  animation: ${pulseAnimation};
  animation-delay: 0.25s;
  &:before: {
    content: ' ';
    display: inline-block;
    position: absolute;
    top: 0;
    width: 10px;
    height: 10px;
    border-radius: 5px;
    background-color: pulseColor;
    color: pulseColor;
    box-shadow: 9984px 0 0 -5px ${pulseColor};
    animation: ${pulseAnimationBefore};
    animation-delay: 0s;
  }

  &:after {
    content: ' ';
    display: inline-block;
    position: absolute;
    top: 0;
    width: 10px;
    height: 10px;
    border-radius: 5px;
    background-color: pulseColor;
    color: pulseColor;
    box-shadow: 10014px 0 0 -5px ${pulseColor};
    animation: ${pulseAnimationAfter};
    animation-delay: 0.5s;
  }
`

export const SettingsIcon = styled.img((width) => ({
  margin: '0 15px',
  width: width || 16,
}))

const AlertButtonWrapper = styled.div(({ cursor }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor,
  position: 'relative',
  [mediaQueries.smallDesktop]: {
    marginRight: '45px',
  },
}))

export const Badge = styled.div((props) => ({
  background: props.color || palette.blue,
  minWidth: '16px',
  height: '16px',
  borderRadius: '10px',
  color: palette.white,
  fontSize: '12px',
  fontFamily: fonts.primaryBook,
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  paddingTop: '1px',
  top: '0px',
  right: '8px',
  [mediaQueries.mobile]: {
    top: '1px',
    right: '-10px',
  },
  ...props.style,
}))

export const ServicesMenuWrapper = styled.div({
  position: 'absolute',
  top: '60px',
  right: '60px',
})

export const AlertButton = ({
  badgeCount = 0,
  onClick,
  hasArrow = false,
  arrowColor,
  arrowDirection,
  cursor = 'default',
}) => (
  <AlertButtonWrapper cursor={cursor} onClick={onClick}>
    <Icon src={alarm} width={24} />
    {badgeCount > 0 && (
      <Badge
        style={{
          [mediaQueries.smallDesktop]: {
            top: '1px',
            right: '-10px',
          },
        }}
      >
        {badgeCount}
      </Badge>
    )}
    {hasArrow && (
      <div style={{ marginLeft: '5px' }}>
        <NavArrowIcon
          up={arrowDirection === 'up'}
          down={arrowDirection === 'down'}
          color={arrowColor}
        />
      </div>
    )}
  </AlertButtonWrapper>
)

const SeeAllAlerts = styled.div({
  width: '100%',
  height: '50px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
})

export const AlertPopups = ({ alerts, onClick, onClickAll }) => {
  const isLoading = !alerts
  const top5Alerts = isLoading || alerts === null ? [] : alerts.slice(0, 5)

  return (
    <>
      {isLoading ? (
        <LoadingResponsive height="90px" />
      ) : (
        <AlertList alerts={top5Alerts} onClick={onClick} />
      )}
      <SeeAllAlerts>
        <SeeMoreSection
          text={translate('see_all_alerts')}
          onClick={onClickAll}
        />
      </SeeAllAlerts>
    </>
  )
}
